// SR-WIRE — Slip Repair session hook.
//
// Read-only consumer of GET /slip-repair-api/session/:id. Mirrors the
// loading/error/refetch shape of useOwlHourSlips. Defensive parsing —
// missing fields fall back to safe defaults; never throws on shape.
//
// Trust contract:
//   - Auth headers come from the shared supabase browser client.
//   - When sessionId is null, the hook returns an idle state and does
//     not fetch.
//   - refetch() is stable for use as a polling trigger.

import { useCallback, useEffect, useMemo, useState } from "react";

import { getBrowserClient } from "../../supabase/browser.ts";

// ─── Row types ─────────────────────────────────────────────────
// These mirror the slip_repair_sessions / slip_repair_legs schema
// (SR-BACK-1). All fields nullable because the row evolves through
// upload → run → user-action stages.

export interface SessionRow {
  session_id: string;
  user_id: string | null;
  created_at: string | null;
  input_source: string | null;
  bookmaker: string | null;
  bookmaker_rules_version: string | null;
  entry_mode: "pre_placement" | "post_build" | null;
  leg_count_original: number | null;
  leg_count_repaired: number | null;
  legs_retained: number | null;
  legs_replaced: number | null;
  legs_flagged: number | null;
  legs_marginal: number | null;
  legs_unresolved: number | null;
  void_high_before: number | null;
  void_high_after: number | null;
  total_odds_original: number | null;
  total_odds_sharp: number | null;
  total_odds_safer: number | null;
  // SR-TIERS
  total_odds_bold: number | null;
  total_odds_moderate: number | null;
  total_odds_safer_tier: number | null;
  total_odds_conservative: number | null;
  health_score_before: number | null;
  health_score_after: number | null;
  mode_selected: "sharp" | "safer" | "both" | null;
  slip_summary: string | null;
  repaired_at: string | null;
  expires_at: string | null;
  share_token: string | null;
  share_expires: string | null;
  user_tier: string | null;
}

export interface LegRow {
  leg_id: string;
  session_id: string;
  leg_number: number;
  orig_match: string | null;
  orig_market: string | null;
  orig_selection: string | null;
  orig_odds: number | null;
  orig_confidence: number | null;
  fixture_id: string | null;
  void_risk: "HIGH" | "MEDIUM" | "LOW" | null;
  void_condition: string | null;
  sharp_decision: "RETAIN" | "REPLACE" | "FLAG" | "MARGINAL" | "UNRESOLVED" | null;
  sharp_rep_market: string | null;
  sharp_rep_selection: string | null;
  sharp_rep_odds: number | null;
  sharp_clv_original: number | null;
  sharp_clv_rep: number | null;
  sharp_delta: number | null;
  sharp_reason: string | null;
  sharp_reason_generated_at: string | null;
  safer_decision: "RETAIN" | "REPLACE" | "FLAG" | "MARGINAL" | "UNRESOLVED" | null;
  safer_rep_market: string | null;
  safer_rep_selection: string | null;
  safer_rep_odds: number | null;
  safer_clv_original: number | null;
  safer_clv_rep: number | null;
  safer_delta: number | null;
  safer_reason: string | null;
  safer_reason_generated_at: string | null;
  // SR-TIERS — JSONB: { bold|moderate|safer|conservative: { market, selection, odds, fair_prob, decision, reason } }
  tier_picks: Record<string, {
    market: string | null;
    selection: string | null;
    odds: number | null;
    fair_prob: number | null;
    decision: "REPLACE" | "RETAIN" | "FLAG" | "UNRESOLVED" | "MARGINAL" | null;
    reason: string | null;
  }> | null;
  user_action: "ACCEPTED" | "OVERRIDDEN" | "REMOVED" | null;
  user_mode_chosen: "sharp" | "safer" | null;
  final_selection: string | null;
  final_odds: number | null;
  created_at: string | null;
  // v3 upgrade engine
  upgrade_market: string | null;
  upgrade_selection: string | null;
  upgrade_odds: number | null;
  upgrade_fair_prob: number | null;
  upgrade_ev_gain: number | null;
  upgrade_reason: string | null;
  upgrade_cross_family: boolean | null;
}

export interface UseSlipRepairResult {
  session: SessionRow | null;
  legs: LegRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─── Defensive parsers ─────────────────────────────────────────

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function parseSession(raw: unknown): SessionRow | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const id = asStr(r.session_id);
  if (!id) return null;
  return {
    session_id: id,
    user_id: asStr(r.user_id),
    created_at: asStr(r.created_at),
    input_source: asStr(r.input_source),
    bookmaker: asStr(r.bookmaker),
    bookmaker_rules_version: asStr(r.bookmaker_rules_version),
    entry_mode: (asStr(r.entry_mode) as SessionRow["entry_mode"]) ?? null,
    leg_count_original: asNum(r.leg_count_original),
    leg_count_repaired: asNum(r.leg_count_repaired),
    legs_retained: asNum(r.legs_retained),
    legs_replaced: asNum(r.legs_replaced),
    legs_flagged: asNum(r.legs_flagged),
    legs_marginal: asNum(r.legs_marginal),
    legs_unresolved: asNum(r.legs_unresolved),
    void_high_before: asNum(r.void_high_before),
    void_high_after: asNum(r.void_high_after),
    total_odds_original: asNum(r.total_odds_original),
    total_odds_sharp: asNum(r.total_odds_sharp),
    total_odds_safer: asNum(r.total_odds_safer),
    total_odds_bold: asNum(r.total_odds_bold),
    total_odds_moderate: asNum(r.total_odds_moderate),
    total_odds_safer_tier: asNum(r.total_odds_safer_tier),
    total_odds_conservative: asNum(r.total_odds_conservative),
    health_score_before: asNum(r.health_score_before),
    health_score_after: asNum(r.health_score_after),
    mode_selected: (asStr(r.mode_selected) as SessionRow["mode_selected"]) ?? null,
    slip_summary: asStr(r.slip_summary),
    repaired_at: asStr(r.repaired_at),
    expires_at: asStr(r.expires_at),
    share_token: asStr(r.share_token),
    share_expires: asStr(r.share_expires),
    user_tier: asStr(r.user_tier),
  };
}

function parseLeg(raw: unknown): LegRow | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const id = asStr(r.leg_id);
  const sid = asStr(r.session_id);
  if (!id || !sid) return null;
  const num = asNum(r.leg_number);
  return {
    leg_id: id,
    session_id: sid,
    leg_number: typeof num === "number" ? num : 0,
    orig_match: asStr(r.orig_match),
    orig_market: asStr(r.orig_market),
    orig_selection: asStr(r.orig_selection),
    orig_odds: asNum(r.orig_odds),
    orig_confidence: asNum(r.orig_confidence),
    fixture_id: asStr(r.fixture_id),
    void_risk: (asStr(r.void_risk) as LegRow["void_risk"]) ?? null,
    void_condition: asStr(r.void_condition),
    sharp_decision: (asStr(r.sharp_decision) as LegRow["sharp_decision"]) ?? null,
    sharp_rep_market: asStr(r.sharp_rep_market),
    sharp_rep_selection: asStr(r.sharp_rep_selection),
    sharp_rep_odds: asNum(r.sharp_rep_odds),
    sharp_clv_original: asNum(r.sharp_clv_original),
    sharp_clv_rep: asNum(r.sharp_clv_rep),
    sharp_delta: asNum(r.sharp_delta),
    sharp_reason: asStr(r.sharp_reason),
    sharp_reason_generated_at: asStr(r.sharp_reason_generated_at),
    safer_decision: (asStr(r.safer_decision) as LegRow["safer_decision"]) ?? null,
    safer_rep_market: asStr(r.safer_rep_market),
    safer_rep_selection: asStr(r.safer_rep_selection),
    safer_rep_odds: asNum(r.safer_rep_odds),
    safer_clv_original: asNum(r.safer_clv_original),
    safer_clv_rep: asNum(r.safer_clv_rep),
    safer_delta: asNum(r.safer_delta),
    safer_reason: asStr(r.safer_reason),
    safer_reason_generated_at: asStr(r.safer_reason_generated_at),
    tier_picks: (r.tier_picks && typeof r.tier_picks === "object" && !Array.isArray(r.tier_picks))
      ? (r.tier_picks as LegRow["tier_picks"])
      : null,
    user_action: (asStr(r.user_action) as LegRow["user_action"]) ?? null,
    user_mode_chosen: (asStr(r.user_mode_chosen) as LegRow["user_mode_chosen"]) ?? null,
    final_selection: asStr(r.final_selection),
    final_odds: asNum(r.final_odds),
    created_at: asStr(r.created_at),
    // v3 upgrade engine
    upgrade_market: asStr(r.upgrade_market),
    upgrade_selection: asStr(r.upgrade_selection),
    upgrade_odds: asNum(r.upgrade_odds),
    upgrade_fair_prob: asNum(r.upgrade_fair_prob),
    upgrade_ev_gain: asNum(r.upgrade_ev_gain),
    upgrade_reason: asStr(r.upgrade_reason),
    upgrade_cross_family: typeof r.upgrade_cross_family === "boolean" ? r.upgrade_cross_family : null,
  };
}

// ─── Hook ──────────────────────────────────────────────────────

const NOOP_REFETCH = async (): Promise<void> => { /* idle */ };

export function useSlipRepair(sessionId: string | null): UseSlipRepairResult {
  const [session, setSession] = useState<SessionRow | null>(null);
  const [legs, setLegs] = useState<LegRow[]>([]);
  const [loading, setLoading] = useState<boolean>(sessionId !== null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(async (): Promise<void> => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setLegs([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const client = getBrowserClient();
        const { data, error: invokeErr } = await client.functions.invoke(
          `slip-repair-api/session/${sessionId}`,
          { method: "GET" },
        );
        if (cancelled) return;
        if (invokeErr) {
          setError(invokeErr.message ?? "Session fetch failed");
          setLoading(false);
          return;
        }
        const env = (data ?? {}) as {
          ok?: boolean;
          session?: unknown;
          legs?: unknown;
          error?: { message?: string };
        };
        if (env.ok === false) {
          setError(env.error?.message ?? "Session fetch returned ok=false");
          setLoading(false);
          return;
        }
        const parsedSession = parseSession(env.session);
        if (!parsedSession) {
          // eslint-disable-next-line no-console
          console.warn("[useSlipRepair] session payload missing or malformed", env.session);
        }
        const parsedLegs: LegRow[] = Array.isArray(env.legs)
          ? (env.legs as unknown[])
              .map(parseLeg)
              .filter((l): l is LegRow => l !== null)
          : [];
        setSession(parsedSession);
        setLegs(parsedLegs);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, tick]);

  return useMemo(
    () => ({
      session,
      legs,
      loading,
      error,
      refetch: sessionId ? refetch : NOOP_REFETCH,
    }),
    [session, legs, loading, error, refetch, sessionId],
  );
}
